import React from "react";
import { Link } from "react-router-dom";
import { PrimaryLink, SecondaryLink } from "./ui/Buttons";
import { ServiceCardLink } from "./ui/ServiceCard";
import { Card } from "./ui/Card";
import { PHONE_TEL } from "../lib/businessInfo";
import { Helmet } from "react-helmet";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Check,
  Phone,
  Wrench,
  Gauge,
  Sparkles,
  Snowflake,
  Disc,
  Cpu,
} from "lucide-react";
import { Reveal } from "./ui/Reveal";
import TrustBadges from "./ui/TrustBadges";
import ReviewSection from "./ui/ReviewSection";
import VehicleBrandGrid from "./ui/VehicleBrandGrid";
import WarrantyNotice from "./ui/WarrantyNotice";
import ServiceAreas from "./ui/ServiceAreas";
import BusinessHours from "./ui/BusinessHours";
import heroShop from "../assets/newshot.jpg";

const heroSupporting =
  "We specialize in Land Rover, European imports, and complex diagnostics — delivering dealer-level care without dealer pricing.";

const europeanOwnerBullets = [
  "Dealer-level diagnostics",
  "Experienced with Land Rover and European imports",
  "Faster turnaround than dealerships",
  "Transparent pricing without surprises",
  "Trusted by local drivers for over 30 years",
];

const services = [
  {
    title: "Smog & Emissions",
    desc: "California emissions testing and repair guidance with accurate diagnostics when readings fail.",
    link: "/services/smog-emissions-services",
    icon: Gauge,
    eyebrow: "State testing",
  },
  {
    title: "Factory Maintenance",
    desc: "Factory-scheduled maintenance for European imports and daily drivers—protect performance and resale value.",
    link: "/services/all-factory",
    icon: Wrench,
    eyebrow: "Preventive care",
  },
  {
    title: "Electrical & Diagnostics",
    desc: "European vehicle diagnostics, charging systems, wiring faults, and advanced scans when the warning lights stay on.",
    link: "/services/electrical-diagnostics",
    icon: Cpu,
    eyebrow: "Problem solving",
  },
  {
    title: "Air Conditioning",
    desc: "A/C inspections, refrigerant service, leak checks, and repair recommendations you can trust.",
    link: "/services/air-conditioning",
    icon: Snowflake,
    eyebrow: "Cabin comfort",
  },
  {
    title: "Brakes & Suspension",
    desc: "Brake service, shocks, struts, and suspension repairs for confidence and control on the road.",
    link: "/services/brake-suspension",
    icon: Disc,
    eyebrow: "Ride & safety",
  },
  {
    title: "Tires & Alignment",
    desc: "Tire support, rotations, balancing, and alignment-related service for smoother daily driving.",
    link: "/services/tire-alignment",
    icon: Sparkles,
    eyebrow: "Daily performance",
  },
];

const heroTrustBadges = [
  "Trusted by local drivers for 30+ years",
  "European vehicle diagnostics",
  "Land Rover service specialists",
];

const popularRepairLinks = [
  {
    title: "Auto Repair",
    desc: "Advanced automotive repair and diagnostics from ASE Master technicians—European imports and daily drivers.",
    to: "/auto-repair",
    icon: Wrench,
    eyebrow: "General repair",
  },
  {
    title: "Check Engine Light",
    desc: "Code scans, testing, and honest answers when your dash warns something is wrong.",
    to: "/check-engine-light",
    icon: Cpu,
    eyebrow: "Diagnostics",
  },
  {
    title: "Transmission Repair",
    desc: "Shifting concerns, fluid service, and drivetrain-related care scoped after diagnosis.",
    to: "/transmission-repair",
    icon: Gauge,
    eyebrow: "Drivetrain",
  },
  {
    title: "European Auto Repair",
    desc: "Maintenance and repair for BMW, Mercedes-Benz, Audi, and other imports.",
    to: "/european-auto-repair",
    icon: Sparkles,
    eyebrow: "Imports",
  },
  {
    title: "Land Rover Repair",
    desc: "SUV service, 4x4 systems, and smog support tailored to Land Rover vehicles.",
    to: "/land-rover-repair",
    icon: Disc,
    eyebrow: "Luxury SUV",
  },
  {
    title: "Jaguar Repair",
    desc: "Diagnostics, maintenance, and repair for Jaguar vehicles from an independent Temecula shop.",
    to: "/jaguar-repair",
    icon: Sparkles,
    eyebrow: "Luxury",
  },
];

const ease = [0.16, 1, 0.3, 1];

export default function Home() {
  const reduceMotion = useReducedMotion();

  const heroContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.07,
        delayChildren: reduceMotion ? 0 : 0.08,
      },
    },
  };

  const heroItem = {
    hidden: {
      opacity: reduceMotion ? 1 : 0,
      y: reduceMotion ? 0 : 14,
      filter: reduceMotion ? "blur(0px)" : "blur(10px)",
    },
    show: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: reduceMotion ? 0 : 0.9, ease },
    },
  };

  const scrollContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.07,
        delayChildren: reduceMotion ? 0 : 0.08,
      },
    },
  };

  const scrollCard = {
    hidden: {
      opacity: reduceMotion ? 1 : 0,
      y: reduceMotion ? 0 : 12,
    },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: reduceMotion ? 0 : 0.55, ease },
    },
  };

  return (
    <>
      <Helmet>
        <title>Advanced Automotive Smog | Smog Check & Auto Repair in Temecula</title>
        <meta
          name="description"
          content="Temecula independent shop specializing in Land Rover, BMW, Mercedes-Benz, Audi, and Jaguar—dealer-level diagnostics and repair without dealer pricing. Star-certified smog, advanced automotive repair."
        />
        <link rel="canonical" href="https://www.advancedautomotivesmog.com/" />
        <meta property="og:title" content="Advanced Automotive Smog | Smog Check & Auto Repair in Temecula" />
        <meta
          property="og:description"
          content="Temecula independent shop specializing in Land Rover, BMW, Mercedes-Benz, Audi, and Jaguar—dealer-level diagnostics and repair without dealer pricing."
        />
        <meta property="og:url" content="https://www.advancedautomotivesmog.com/" />
        <meta property="og:image" content="https://www.advancedautomotivesmog.com/assets/home.jpeg" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Advanced Automotive Smog" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Advanced Automotive Smog | Smog Check & Auto Repair in Temecula" />
        <meta
          name="twitter:description"
          content="Temecula independent shop specializing in Land Rover, BMW, Mercedes-Benz, Audi, and Jaguar—dealer-level diagnostics and repair without dealer pricing."
        />
        <meta name="twitter:image" content="https://www.advancedautomotivesmog.com/assets/home.jpeg" />
      </Helmet>

      <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroShop}
              alt="Advanced Automotive Smog shop exterior in Temecula"
              className="h-full w-full object-cover object-center sm:object-[center_40%]"
              loading="eager"
              decoding="async"
              draggable={false}
              style={{
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
              }}
            />
            <div
              className="absolute inset-0 bg-navy-900/[0.72]"
              aria-hidden
            />
            <div
              className="absolute inset-0 bg-gradient-to-r from-black/45 via-navy-950/30 to-black/10 sm:from-black/35"
              aria-hidden
            />
            <div
              className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30"
              aria-hidden
            />
          </div>

          <motion.div
            variants={heroContainer}
            initial="hidden"
            animate="show"
            className="relative mx-auto flex min-h-[min(100dvh,880px)] max-w-6xl flex-col justify-center px-4 pb-12 pt-8 sm:min-h-[88vh] sm:px-5 sm:pb-20 sm:pt-14 lg:min-h-[90vh] lg:px-10 lg:pb-24 lg:pt-16"
          >
            <div className="max-w-2xl">
              <motion.p
                variants={heroItem}
                className="inline-flex max-w-full flex-wrap items-center gap-x-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-white/90 backdrop-blur-sm sm:text-xs"
              >
                <span>Advanced Automotive Smog</span>
                <span className="hidden text-white/40 sm:inline" aria-hidden>
                  ·
                </span>
                <span className="text-white/80">Temecula, CA</span>
              </motion.p>

              <motion.h1
                variants={heroItem}
                className="mt-4 text-[clamp(1.65rem,5.2vw,2.05rem)] font-semibold leading-[1.08] tracking-tight text-white sm:mt-5 sm:text-[2.85rem] sm:leading-[1.06] lg:text-[3.45rem]"
              >
                <span className="block drop-shadow-[0_2px_28px_rgba(0,0,0,0.35)]">
                  <span className="text-teal-500">Premium</span> Auto Care.
                </span>
                <span className="mt-3 block text-[0.82em] font-normal leading-snug text-white/85 sm:mt-4">
                  Without the premium price.
                </span>
              </motion.h1>

              <motion.p
                variants={heroItem}
                className="mt-5 max-w-xl text-[14px] font-normal leading-relaxed text-white/90 sm:text-lg"
              >
                {heroSupporting}
              </motion.p>

              <motion.div
                variants={heroItem}
                className="mt-8 flex w-full max-w-lg flex-col gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center"
              >
                <PrimaryLink
                  href={PHONE_TEL}
                  data-gtm="phone-click"
                  className="w-full justify-center gap-2 px-6 py-3.5 text-[15px] sm:w-auto sm:min-w-[12rem] sm:px-8 sm:text-base motion-reduce:transition-none"
                >
                  <Phone size={18} className="shrink-0" aria-hidden />
                  Call Now
                </PrimaryLink>
                <SecondaryLink
                  to="/services"
                  className="w-full justify-center gap-2 border-white/35 bg-white/5 px-6 py-3.5 text-[15px] text-white hover:border-white hover:bg-white hover:text-navy-900 sm:w-auto sm:min-w-[12rem] sm:px-8 sm:text-base motion-reduce:transition-none"
                >
                  View Services
                  <ArrowRight size={18} className="shrink-0" aria-hidden />
                </SecondaryLink>
              </motion.div>

              <motion.div variants={heroItem}>
                <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50 sm:mt-10">
                  Why European & premium owners choose us
                </p>
                <ul
                  className="mt-3 grid grid-cols-1 gap-2.5 sm:mt-4 sm:grid-cols-3 sm:gap-3"
                  aria-label="Trust highlights"
                >
                  {heroTrustBadges.map((label) => (
                    <li
                      key={label}
                      className="flex items-start gap-2.5 rounded-xl border border-white/12 bg-white/[0.07] px-3 py-3 backdrop-blur-sm sm:flex-col sm:gap-2 sm:px-4 sm:py-3.5"
                    >
                      <Check
                        className="mt-0.5 h-4 w-4 shrink-0 text-teal-500 sm:mt-0"
                        strokeWidth={2.5}
                        aria-hidden
                      />
                      <span className="text-left text-[13px] font-medium leading-snug text-white sm:text-sm">
                        {label}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </motion.div>
        </section>

        <section className="border-t border-slate-200 bg-[#EEF2F7] text-slate-700">
          <div className="mx-auto max-w-6xl px-4 py-14 md:py-20 sm:px-5 lg:px-8">
            <h2 className="text-center text-[1.5rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-3xl">
              Trusted Specialists for European &amp; High-End Vehicles
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-sm font-normal leading-relaxed text-slate-700 sm:text-[15px]">
              We provide dealer-level diagnostics and repair for premium vehicles — without the
              dealer pricing.
            </p>
            <VehicleBrandGrid hideHeading className="mt-10" tone="light" />
          </div>
        </section>

        <section className="border-t border-slate-200 bg-white text-slate-700">
          <div className="mx-auto max-w-6xl px-4 py-14 md:py-20 sm:px-5 lg:px-8">
            <div className="grid gap-10 md:grid-cols-2 md:items-center md:gap-12 lg:gap-16">
              <div className="border-l-4 border-slate-300 pl-4 sm:pl-5">
                <h2 className="text-[1.35rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-2xl">
                  Why European Vehicle Owners Choose Us
                </h2>
                <ul className="mt-6 space-y-4">
                  {europeanOwnerBullets.map((line) => (
                    <li key={line} className="flex gap-3">
                      <Check
                        className="mt-0.5 h-5 w-5 shrink-0 text-teal-600"
                        strokeWidth={2.5}
                        aria-hidden
                      />
                      <span className="text-sm font-normal leading-relaxed text-slate-700 sm:text-[15px]">
                        {line}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="md:justify-self-end">
                <img
                  src={heroShop}
                  alt="Advanced Automotive Smog service bay and technicians at work"
                  className="w-full rounded-xl border border-slate-200 shadow-sm object-cover aspect-[4/3] md:max-w-lg md:ml-auto"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 bg-[#EEF2F7] text-slate-700">
          <Reveal className="relative mx-auto max-w-6xl px-4 py-10 sm:px-5 sm:py-14 lg:px-8 lg:py-20">
            <div className="mb-10 flex flex-col gap-6 sm:mb-12 sm:flex-row sm:items-end sm:justify-between">
              <div className="max-w-2xl border-l-4 border-slate-300 pl-4 sm:pl-5">
                <span className="text-[12px] font-medium tracking-normal text-slate-500">
                  Services
                </span>

                <h2 className="mt-2 text-[1.75rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-3xl">
                  European vehicle diagnostics, smog &amp; advanced repair.
                </h2>

                <p className="mt-4 max-w-2xl text-sm font-normal leading-relaxed text-slate-700 sm:text-[15px]">
                  From emissions certification to Land Rover drivetrain work—browse services built
                  for complex diagnostics and honest estimates.
                </p>
              </div>

              <Link
                to="/services"
                className="hidden items-center justify-center gap-2 rounded-lg border-2 border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-teal-600 shadow-sm transition-colors hover:border-teal-600 hover:bg-teal-50 sm:inline-flex"
              >
                Browse all services{" "}
                <ArrowRight size={16} className="text-teal-600" aria-hidden />
              </Link>
            </div>

            <div className="hidden gap-6 sm:grid sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {services.map((service) => {
                const Icon = service.icon;

                return (
                  <ServiceCardLink
                    key={service.title}
                    to={service.link}
                    className="group relative overflow-hidden p-6 sm:p-8"
                  >
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(20,184,166,0.08),transparent_36%)] opacity-0 transition-all duration-300 ease-in-out group-hover:opacity-100" />

                    <div className="relative z-10">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-medium tracking-normal text-slate-500">
                          {service.eyebrow}
                        </span>

                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-teal-600 transition-all duration-300 ease-in-out group-hover:scale-[1.03] group-hover:bg-teal-50">
                          <Icon size={19} strokeWidth={1.85} />
                        </div>
                      </div>

                      <h3 className="mt-5 text-lg font-semibold leading-snug tracking-tight text-navy-900 sm:text-xl">
                        {service.title}
                      </h3>

                      <p className="mt-3 text-[14px] font-normal leading-relaxed text-slate-700">
                        {service.desc}
                      </p>

                      <div className="mt-7 flex items-center justify-between">
                        <span className="text-sm font-medium text-teal-600 transition-colors duration-300 group-hover:text-teal-700">
                          Learn more
                        </span>

                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors duration-300 ease-in-out group-hover:text-teal-600">
                          <ArrowRight
                            size={16}
                            className="transition-transform duration-300 group-hover:translate-x-1 motion-reduce:group-hover:translate-x-0"
                            aria-hidden
                          />
                        </span>
                      </div>
                    </div>
                  </ServiceCardLink>
                );
              })}
            </div>

            <ServiceAreas
              className="mt-12 hidden sm:block lg:mt-14"
              tone="light"
            />

            <motion.div
              variants={scrollContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              className="space-y-4 sm:hidden"
            >
              {services.map((service) => {
                const Icon = service.icon;

                return (
                  <motion.div key={service.title} variants={scrollCard}>
                    <ServiceCardLink
                      to={service.link}
                      className="group relative block overflow-hidden p-5 transition-all duration-300 ease-in-out active:scale-[0.99]"
                    >
                      <div className="relative z-10">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-start gap-3.5">
                            <div className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-teal-600">
                              <Icon size={20} strokeWidth={1.85} />
                            </div>

                            <div className="min-w-0">
                              <p className="text-[10px] font-medium tracking-normal text-slate-500">
                                {service.eyebrow}
                              </p>

                              <h3 className="mt-1.5 text-[15px] font-semibold leading-snug tracking-tight text-navy-900">
                                {service.title}
                              </h3>

                              <p className="mt-2 text-[12px] font-normal leading-relaxed text-slate-700">
                                {service.desc}
                              </p>
                            </div>
                          </div>

                          <div className="flex h-8 w-8 shrink-0 items-center justify-center text-slate-400 transition-colors duration-300 ease-in-out group-hover:text-teal-600">
                            <ArrowRight
                              size={15}
                              className="transition-transform duration-300 group-hover:translate-x-1 motion-reduce:group-hover:translate-x-0"
                              aria-hidden
                            />
                          </div>
                        </div>
                      </div>
                    </ServiceCardLink>
                  </motion.div>
                );
              })}

              <ServiceAreas className="sm:hidden" tone="light" />
            </motion.div>
          </Reveal>
        </section>

        <section className="border-t border-slate-200 bg-white text-slate-700">
          <div className="mx-auto max-w-6xl px-4 py-10 sm:px-5 sm:py-14 lg:px-8 lg:py-20">
            <TrustBadges tone="light" />
            <VehicleBrandGrid className="mt-10" tone="light" linked />
            <WarrantyNotice className="mt-8 max-w-2xl" tone="light" />
          </div>
        </section>

        <section className="border-t border-slate-200 bg-[#F8F9FA] text-slate-700">
          <Reveal className="relative mx-auto max-w-6xl px-4 py-10 sm:px-5 sm:py-14 lg:px-8 lg:py-20">
            <div className="max-w-2xl border-l-4 border-slate-300 pl-4 sm:pl-5">
              <span className="text-[12px] font-medium tracking-normal text-slate-500">
                Popular
              </span>
              <h2 className="mt-2 text-[1.35rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-2xl">
                Popular Repair Services
              </h2>
              <p className="mt-3 max-w-2xl text-sm font-normal leading-relaxed text-slate-700 sm:text-[15px]">
                Deep-dive pages for European imports, Land Rover, transmissions, and advanced
                diagnostics—tap through for scope and pricing expectations.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 xl:grid-cols-3">
              {popularRepairLinks.map((item) => {
                const Icon = item.icon;
                return (
                  <ServiceCardLink
                    key={item.to}
                    to={item.to}
                    className="group relative overflow-hidden p-6 sm:p-8"
                  >
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(20,184,166,0.08),transparent_36%)] opacity-0 transition-all duration-300 ease-in-out group-hover:opacity-100" />

                    <div className="relative z-10">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-medium tracking-normal text-slate-500">
                          {item.eyebrow}
                        </span>

                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-teal-600 transition-all duration-300 ease-in-out group-hover:scale-[1.03] group-hover:bg-teal-50">
                          <Icon size={19} strokeWidth={1.85} />
                        </div>
                      </div>

                      <h3 className="mt-5 text-lg font-semibold leading-snug tracking-tight text-navy-900 sm:text-xl">
                        {item.title}
                      </h3>

                      <p className="mt-3 text-[14px] font-normal leading-relaxed text-slate-700">
                        {item.desc}
                      </p>

                      <div className="mt-7 flex items-center justify-between">
                        <span className="text-sm font-medium text-teal-600 transition-colors duration-300 group-hover:text-teal-700">
                          Learn more
                        </span>

                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors duration-300 ease-in-out group-hover:text-teal-600">
                          <ArrowRight
                            size={16}
                            className="transition-transform duration-300 group-hover:translate-x-1 motion-reduce:group-hover:translate-x-0"
                            aria-hidden
                          />
                        </span>
                      </div>
                    </div>
                  </ServiceCardLink>
                );
              })}
            </div>

            <div className="mt-12 rounded-2xl border border-slate-200/80 bg-[#EEF2F7] px-6 py-10 text-center sm:mt-14 sm:px-10 sm:py-12">
              <h3 className="text-xl font-semibold tracking-tight text-navy-900 sm:text-2xl">
                Dealer-Level Capability. Independent Shop Pricing.
              </h3>
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-700 sm:text-[15px]">
                From Land Rover drivetrain issues to advanced diagnostics, we handle the repairs
                other shops avoid.
              </p>
              <div className="mt-8 flex justify-center">
                <PrimaryLink
                  to="/european-auto-repair"
                  className="inline-flex gap-2 px-8 py-3.5 text-[15px] sm:text-base"
                >
                  Explore European Vehicle Services
                  <ArrowRight size={18} className="shrink-0" aria-hidden />
                </PrimaryLink>
              </div>
            </div>
          </Reveal>
        </section>

        <section className="border-t border-slate-200 bg-[#EEF2F7]">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 py-10 sm:px-5 sm:py-14 lg:px-8 lg:py-20">
            <ReviewSection tone="light" />
          </div>
        </section>

        <section className="border-t border-slate-200 bg-white text-slate-700">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 py-10 sm:px-5 sm:py-14 lg:px-8 lg:py-20">
            <BusinessHours className="max-w-md" tone="light" />

            <motion.div
              variants={scrollContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              className="mt-8 max-w-full sm:max-w-xl"
            >
              <motion.div variants={scrollCard}>
                <Card padding="none" className="p-5 sm:p-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-[14px] font-medium leading-snug text-navy-900">
                        Need help choosing a service?
                      </p>
                      <p className="mt-1.5 text-[12px] font-normal leading-relaxed text-slate-600 sm:text-sm">
                        Tell us your vehicle and symptoms—we&apos;ll route you to European vehicle
                        diagnostics, smog, or the right repair lane.
                      </p>
                    </div>

                    <span className="shrink-0 self-start rounded-xl bg-slate-100 px-2.5 py-1 text-[10px] font-medium text-teal-700">
                      Fast help
                    </span>
                  </div>

                  <div className="mt-5 flex flex-col gap-2.5 sm:grid sm:grid-cols-2 sm:gap-3">
                    <PrimaryLink
                      href={PHONE_TEL}
                      data-gtm="phone-click"
                      className="w-full justify-center gap-2 py-3 text-[13px] sm:text-sm"
                    >
                      <Phone size={14} className="shrink-0" aria-hidden />
                      Call now
                    </PrimaryLink>

                    <SecondaryLink
                      to="/services"
                      className="w-full justify-center gap-2 py-3 text-[13px] sm:text-sm"
                    >
                      Browse all
                      <ArrowRight size={14} className="shrink-0" aria-hidden />
                    </SecondaryLink>
                  </div>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </section>
    </>
  );
}